/**
 * useAuth Hook - Authentication management with Keycloak (OIDC)
 *
 * Wraps react-oidc-context's useAuth() and re-shapes it into the SAME
 * ten-key contract every one of the SPA's ~76 `getAccessToken` callers
 * already depends on (plan §6.1):
 *
 *   { isAuthenticated, isLoading, account, accessToken, error,
 *     login, logout, getAccessToken, getUserClaims, isTokenValid }
 *
 * Only the implementation underneath changed. Changing this contract would
 * be a 76-file change with 76 chances to break a page nobody opens until
 * Friday, so it stays byte-identical. See the UseAuthResult typedef below
 * for the exact shape.
 *
 * One exception to "underneath": when the build-time dev-auth shim is
 * active (../config/devAuth.js — REACT_APP_ALLOW_DEV_TOKENS plus an
 * injected `e2e_dev_token`), this hook returns the same ten keys sourced
 * from that token instead of react-oidc-context. See devAuth.js's module
 * docstring for why that shim is safe and is NOT the deleted MSAL
 * createFallbackToken().
 */

import { useCallback, useMemo } from 'react';
import { useAuth as useOidcAuth } from 'react-oidc-context';
import { jwtDecode } from 'jwt-decode';
import { buildSigninRedirectArgs } from '../config/authConfig';
import { isDevAuthEnabled, getDevToken } from '../config/devAuth';

/**
 * A Keycloak ID token's decoded claims, the subset this hook reads.
 * @typedef {Object} OidcProfile
 * @property {string} sub - Stable subject identifier. Plays the role Entra's `oid` played.
 *   The one claim OIDC guarantees present; everything else here is optional.
 * @property {string} [email]
 * @property {string} [name]
 * @property {string} [preferred_username]
 * @property {string[]} [organization] - Multi-valued; never index into this outside §2.3's resolver.
 */

/**
 * The value useAuth() returns — the ten-key contract every one of the
 * SPA's ~76 getAccessToken callers depends on. This shape must stay
 * byte-identical across implementation swaps (see module docstring).
 * @typedef {Object} UseAuthResult
 * @property {boolean} isAuthenticated
 * @property {boolean} isLoading
 * @property {{username: (string|undefined), name: (string|undefined), localAccountId: string}|null} account
 *   Minimal replacement for MSAL's AccountInfo. Every current consumer
 *   (admin/UserManagement.js, admin/ProjectManagement.js,
 *   admin/PolicyManagement.js) only checks truthiness (`if (!account)`), so
 *   this three-field shape is sufficient today — pinned here so the next
 *   consumer that reads a field off it knows what's actually guaranteed.
 * @property {string|null} accessToken - The current Keycloak ACCESS token
 *   (never the ID token — see getAccessToken), or null.
 * @property {string|null} error - The message from react-oidc-context's most
 *   recent navigator error. `login()`, `logout()`, and `getAccessToken()`'s
 *   silent-renew path all call react-oidc-context's wrapped
 *   signinRedirect/signoutRedirect/signinSilent, which never reject to the
 *   caller — on failure they dispatch into this shared error state and
 *   resolve to null/undefined instead (verified against the installed
 *   react-oidc-context source: its navigatorKeys wrapper catches, dispatches
 *   `{type: "ERROR", ...}`, and returns null). So all three failure paths
 *   surface here automatically; the try/catch blocks around them below are
 *   a defensive backstop, not the primary reporting path.
 * @property {() => Promise<void>} login
 * @property {() => Promise<void>} logout
 * @property {(forceRefresh?: boolean) => Promise<string|null>} getAccessToken
 * @property {() => ({email: (string|undefined), name: (string|undefined), oid: string, preferred_username: (string|undefined)}|null)} getUserClaims
 * @property {() => boolean} isTokenValid
 */

/**
 * Check if a token is valid and not expired. Pure function, no hooks.
 * @param {string|null|undefined} token
 * @returns {boolean}
 */
const validateToken = (token) => {
  if (!token) {
    return false;
  }

  try {
    const decoded = jwtDecode(token);
    const currentTime = Math.floor(Date.now() / 1000);

    return decoded.exp > currentTime;
  } catch (err) {
    console.warn('Failed to validate token:', err);
    return false;
  }
};

/**
 * Decode a dev-auth token's claims into the same shape the OIDC path
 * trusts. Boundary validation: this token was written into localStorage by
 * the Playwright suite, not by this SPA, so it is an external payload the
 * same way an OIDC profile is — decoded defensively and rejected (returns
 * null) rather than trusted blind, exactly like getUserClaims below
 * requires `profile.sub` before trusting an OIDC profile.
 * @param {string|null|undefined} token
 * @returns {({sub: string, email: (string|undefined), name: (string|undefined), preferred_username: (string|undefined), exp: (number|undefined)}|null)}
 */
const decodeDevTokenClaims = (token) => {
  if (!token) {
    return null;
  }

  try {
    const decoded = jwtDecode(token);
    if (!decoded || typeof decoded.sub !== 'string' || decoded.sub.length === 0) {
      return null;
    }
    return decoded;
  } catch (err) {
    console.warn('devAuth: failed to decode injected token', err);
    return null;
  }
};

/** Dev-auth login/logout are no-ops: the token is already present when
 * this path is active, so there is nothing for these to redirect to. */
const devAuthNoop = async () => {};

/**
 * MODULE-scoped (not a hook ref): shared across EVERY useAuth() call site in
 * the app, not just the same component. Neither oidc-client-ts nor
 * react-oidc-context deduplicates concurrent signinSilent() calls (checked:
 * no mutex/lock/inFlight/queue in oidc-client-ts), so without this, N of the
 * ~76 components hitting an expired access token in the same tick fire N
 * parallel silent-renewal requests, each presenting the same refresh token.
 *
 * That is not benign here: the committed realm sets
 * `revokeRefreshToken: true` with `refreshTokenMaxReuse` absent (defaults to
 * 0 — zero reuse permitted; infra/keycloak/realm-platform.json, asserted at
 * backend/tests/deploy/test_keycloak_realm_platform.py:86). The FIRST
 * concurrent call rotates the refresh token; every sibling call then
 * presents an already-revoked one and fails. With `accessTokenLifespan`
 * 600s, a page load after ten minutes idle is an ordinary event across 76
 * possible callers — this is what stops it from killing the session.
 *
 * @type {Promise<import('oidc-client-ts').User|null>|null}
 */
let inFlightSilentRenew = null;

/**
 * Authentication hook providing auth state and methods.
 * @returns {UseAuthResult}
 */
export const useAuth = () => {
  // Dev-auth shim (see ../config/devAuth.js's docstring for the three
  // security properties that make this distinct from the deleted MSAL
  // createFallbackToken()). Read unconditionally, in the same hook-call
  // order every render, so this branch never violates rules-of-hooks —
  // only the RETURN below picks between the two computed results.
  // `isDevAuthEnabled()`'s value is fixed at build time
  // (process.env.REACT_APP_ALLOW_DEV_TOKENS is a CRA build-time
  // substitution, never a runtime read), so no input reachable after the
  // bundle is built can change which branch runs. With the flag unset
  // (this repo's production build) the comparison evaluates against
  // `undefined` on every render and is always false, so `getDevToken()` —
  // a localStorage read — is never reached, though the comparison itself
  // still executes; this is NOT dead-code elimination.
  const devToken = isDevAuthEnabled() ? getDevToken() : null;
  const devClaims = decodeDevTokenClaims(devToken);
  // Eligible only with a token that decoded to a real subject AND has not
  // expired — an injected-but-stale or malformed token must never
  // authenticate, the same standard the OIDC path holds itself to.
  const useDevAuth = Boolean(devClaims) && validateToken(devToken);

  const oidcAuth = useOidcAuth();
  const { user, isAuthenticated: oidcIsAuthenticated, isLoading: oidcIsLoading, signinRedirect, signoutRedirect, signinSilent } = oidcAuth;

  // ALWAYS the Keycloak access token, never the ID token. An ID token's
  // `aud` is this SPA's own client id, not a resource server's — the SPA
  // used to send the Microsoft ID token, which worked only by coincidence
  // of the audience being the SPA's own client id. The access token is what
  // §4.3's audience mapper stamps with the backend client id, which is what
  // RFC 9700 expects and what makes the backend's audience check mean what
  // it says. See useAuth.getAccessToken.test.js for the regression test.
  const accessToken = user?.access_token ?? null;

  const error = oidcAuth.error ? oidcAuth.error.message : null;

  const account = useMemo(() => {
    const profile = user?.profile;
    if (!profile) {
      return null;
    }
    return {
      username: profile.preferred_username || profile.email,
      name: profile.name || profile.email,
      localAccountId: profile.sub,
    };
  }, [user]);

  /**
   * Check if current token is valid and not expired.
   */
  const isTokenValid = useCallback(() => {
    return validateToken(accessToken);
  }, [accessToken]);

  /**
   * Initiate login flow. Carries the current URL through the round-trip via
   * OIDC `state` (see authConfig.js#buildSigninRedirectArgs) — the
   * equivalent of MSAL's `navigateToLoginRequestUrl: true`, which
   * /cli-auth's single-use challenge parameters depend on.
   *
   * See UseAuthResult's `error` doc: signinRedirect never rejects here —
   * this catch is a defensive backstop, not how failures actually surface.
   */
  const login = useCallback(async () => {
    try {
      await signinRedirect(buildSigninRedirectArgs());
    } catch (err) {
      console.error('Login failed:', err);
    }
  }, [signinRedirect]);

  /**
   * Logout user. RP-initiated logout ends the Keycloak SSO session, not
   * just the local SPA session — parity with MSAL's `logoutRedirect`. See
   * UseAuthResult's `error` doc for how a failure here actually surfaces.
   */
  const logout = useCallback(async () => {
    try {
      await signoutRedirect();
    } catch (err) {
      console.error('Logout failed:', err);
    }
  }, [signoutRedirect]);

  /**
   * Get access token for API calls.
   *
   * With `automaticSilentRenew: true` (authConfig.js), react-oidc-context
   * keeps `user.access_token` fresh in the background, so the common path
   * is a synchronous read. If the cached token has nonetheless expired
   * (e.g. the renewal race), fall back to one explicit `signinSilent()` —
   * shared via the module-scoped `inFlightSilentRenew` so concurrent
   * callers across every component collapse into a single request instead
   * of racing to rotate the same refresh token (see its doc above).
   *
   * @param {boolean} [forceRefresh=false]
   * @returns {Promise<string|null>}
   */
  const getAccessToken = useCallback(async (forceRefresh = false) => {
    if (!user) {
      console.log('useAuth: No user available for token');
      return null;
    }

    if (!forceRefresh && user.access_token && !user.expired) {
      return user.access_token;
    }

    if (!inFlightSilentRenew) {
      inFlightSilentRenew = (async () => {
        try {
          return await signinSilent();
        } finally {
          inFlightSilentRenew = null;
        }
      })();
    }

    try {
      const refreshed = await inFlightSilentRenew;
      if (refreshed?.access_token) {
        return refreshed.access_token;
      }
      console.error('useAuth: Silent renewal returned no access token');
      return user.access_token ?? null;
    } catch (err) {
      console.error('useAuth: Silent token renewal failed:', err);
      // Best-effort: use whatever is cached rather than fail outright.
      // This is NOT the deleted createFallbackToken() minter — it never
      // fabricates a token, only returns one the IdP actually issued.
      return user.access_token ?? null;
    }
  }, [user, signinSilent]);

  /**
   * Get user claims, synthesised from the Keycloak ID token's profile.
   *
   * Boundary validation: `profile.sub` is the one OIDC claim guaranteed
   * present, so a profile missing it is not one this hook trusts (returns
   * null). `email`/`name`/`preferred_username` are all OPTIONAL OIDC
   * claims — a brokered customer IdP (§5) is not guaranteed to release
   * `email` — so each falls back across the others rather than silently
   * becoming `undefined` where a naive read would assume a string.
   *
   * @returns {({email: (string|undefined), name: (string|undefined), oid: string, preferred_username: (string|undefined)}|null)}
   */
  const getUserClaims = useCallback(() => {
    const profile = user?.profile;
    if (!profile || typeof profile.sub !== 'string' || profile.sub.length === 0) {
      return null;
    }

    const email = profile.email || profile.preferred_username || undefined;
    const preferredUsername = profile.preferred_username || profile.email || undefined;

    return {
      email,
      name: profile.name || email || preferredUsername,
      // Stable subject — the same role Entra's `oid` played. NOTE: this
      // changes the VALUE (not just the source) of what was `oid` under
      // MSAL, because it is a different identifier system. The
      // upload-disclaimer localStorage key (UploadDisclaimerModal.js) is
      // derived from `oid`, so every user is re-prompted once after
      // cutover — harmless, but expect it reported as a bug on cutover
      // morning (B-7 comms).
      oid: profile.sub,
      // `tid` is intentionally DROPPED, not carried across renamed.
      // Keycloak's `organization` claim is multi-valued; `[0]` would pick
      // an arbitrary member and make this a lie by omission. Nothing in
      // src/ reads `tid` (verified — the only references were this file
      // and the now-deleted debug fallback token), and tenant resolution
      // is §2.3's job on the backend, not this claim's.
      preferred_username: preferredUsername,
    };
  }, [user]);

  // --- Dev-auth derived values. Computed unconditionally (same
  // rules-of-hooks reasoning as above); only used when useDevAuth is true.

  const devAccount = useMemo(() => {
    if (!devClaims) {
      return null;
    }
    const email = devClaims.email || devClaims.preferred_username || undefined;
    return {
      username: devClaims.preferred_username || email,
      name: devClaims.name || email,
      localAccountId: devClaims.sub,
    };
  }, [devClaims]);

  /** Returns the injected dev token verbatim — never mints one. */
  const devGetAccessToken = useCallback(async () => devToken, [devToken]);

  const devGetUserClaims = useCallback(() => {
    if (!devClaims) {
      return null;
    }
    const email = devClaims.email || devClaims.preferred_username || undefined;
    const preferredUsername = devClaims.preferred_username || devClaims.email || undefined;
    return {
      email,
      name: devClaims.name || email || preferredUsername,
      oid: devClaims.sub,
      preferred_username: preferredUsername,
    };
  }, [devClaims]);

  const devIsTokenValid = useCallback(() => validateToken(devToken), [devToken]);

  if (useDevAuth) {
    return {
      // Authentication state
      isAuthenticated: true,
      isLoading: false,
      account: devAccount,
      accessToken: devToken,
      error: null,

      // Authentication methods
      login: devAuthNoop,
      logout: devAuthNoop,
      getAccessToken: devGetAccessToken,
      getUserClaims: devGetUserClaims,
      isTokenValid: devIsTokenValid,
    };
  }

  return {
    // Authentication state
    isAuthenticated: oidcIsAuthenticated,
    isLoading: oidcIsLoading,
    account,
    accessToken,
    error,

    // Authentication methods
    login,
    logout,
    getAccessToken,
    getUserClaims,
    isTokenValid,
  };
};
